import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Logo } from '@/ui/components/logo';
import { Button } from '@/ui/components/button';
import { Form } from '@/ui/components/form';
import { Field, FieldControl, FieldError, FieldLabel } from '@/ui/components/form/field';
import { useCreateOrganization } from '@/features/organizations/mutations';
import { useMutation } from '@tanstack/react-query';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import * as React from 'react';
import bg from '@/assets/bg.png';
import { sharepointConnectionStatusQuery } from '@/features/sharepoint/queries';
import { procoreConnectionStatusQuery } from '@/features/procore/queries';
import { PROCORE_ENABLED } from '@/features/procore/constants';
import { useQuery } from '@tanstack/react-query';
import { useSharepointLogin } from '@/features/sharepoint/helpers';
import { Icon } from '@/ui/components/icon';
import { useProcoreLogin } from '@/features/procore/helpers';
import { ArrowRight02Icon, Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { updateUserProfile } from '@/features/user/api';
import { Fieldset, FieldsetLegend } from '@/ui/components/form/fieldset';
import { Spinner } from '@/ui/components/spinner';
import { useAuth } from '@/features/auth';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/ui/components/alert-dialog';
import { Icons } from '@/ui/components/icons';

export const Route = createFileRoute('/_authed/add-organization')({
  component: RouteComponent,
});

const stepAtom = atomWithStorage('add_organization_step', 0);
const organizationNameAtom = atom('');

function RouteComponent() {
  const auth = useAuth();
  const step = useAtomValue(stepAtom);

  const hasName = Boolean(auth.user.firstName?.trim() && auth.user.lastName?.trim());
  const storedStep = step;
  const currentStep = hasName ? 1 : storedStep;
  const content = [<UserDetailsStep key="0" />, <OrganizationStep key="1" />];

  return (
    <div className="grid lg:grid-cols-[28rem_1fr] h-svh grow">
      <div className="p-5 md:p-7 flex flex-col">
        <Logo className="mb-5 md:mb-8" />
        <p className="font-medium text-muted mb-5">
          <strong className="text-foreground"> {hasName ? 1 : currentStep + 1}</strong> /{' '}
          {hasName ? 1 : content.length}
        </p>
        {content[currentStep]}
      </div>
      <img src={bg} alt="" className="size-full h-svh max-lg:hidden object-cover" />
    </div>
  );
}

function UserDetailsStep() {
  const auth = useAuth();
  const setStep = useSetAtom(stepAtom);
  const [firstName, setFirstName] = React.useState(auth.user.firstName || '');
  const [lastName, setLastName] = React.useState(auth.user.lastName || '');

  React.useEffect(() => {
    if (firstName === '' && lastName === '') {
      setFirstName(auth.user.firstName || '');
      setLastName(auth.user.lastName || '');
    }
  }, [firstName, lastName, auth.user]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: { firstName: string; lastName: string }) => {
      await updateUserProfile({
        ...payload,
        systemLanguage: auth.user.systemLanguage,
        defaultTranslationLanguage: auth.user.defaultTranslationLanguage,
      });
    },
    onSuccess: () => setStep((prev) => prev + 1),
  });

  return (
    <>
      <Form
        onFormSubmit={() => {
          if (firstName === auth.user.firstName && lastName === auth.user.lastName) {
            setStep((prev) => prev + 1);
            return;
          }
          mutate({ firstName, lastName });
        }}
        className="space-y-3 md:space-y-5 grow flex flex-col"
      >
        <Fieldset>
          <FieldsetLegend>
            {t({ en: 'Help us get to know you', nb: 'Hjelp oss med å bli kjent med deg' })}
          </FieldsetLegend>
          <Field>
            <FieldLabel>{t({ en: 'First name', nb: 'Fornavn' })}</FieldLabel>
            <FieldControl
              value={firstName}
              onValueChange={setFirstName}
              required
              placeholder={t({ en: 'First name', nb: 'Fornavn' })}
            />
            <FieldError />
          </Field>
          <Field>
            <FieldLabel>{t({ en: 'Last name', nb: 'Etternavn' })}</FieldLabel>
            <FieldControl
              value={lastName}
              onValueChange={setLastName}
              required
              placeholder={t({ en: 'Last name', nb: 'Etternavn' })}
            />
            <FieldError />
          </Field>
          <Field>
            <FieldLabel>{t({ en: 'Email', nb: 'E-post' })}</FieldLabel>
            <FieldControl
              value={auth.user.email}
              readOnly
              placeholder={t({ en: 'Email', nb: 'E-post' })}
            />
          </Field>
        </Fieldset>
        <Button type="submit" size="lg" className="w-full mt-auto" pending={isPending}>
          {t(common.continue)}
          <Icon icon={ArrowRight02Icon} />
        </Button>
      </Form>
    </>
  );
}

function OrganizationStep() {
  const auth = useAuth();
  const navigate = useNavigate();
  const setStep = useSetAtom(stepAtom);
  const [organizationName, setOrganizationName] = useAtom(organizationNameAtom);
  const sharepointLogin = useSharepointLogin();
  const procoreLogin = useProcoreLogin();
  const { data: sharepoint, isLoading: sharepointLoading } = useQuery(
    sharepointConnectionStatusQuery(),
  );
  const { data: procore, isLoading: procoreLoading } = useQuery({
    ...procoreConnectionStatusQuery(),
    enabled: PROCORE_ENABLED,
  });
  const isLoading = sharepointLoading || procoreLoading;
  const hasName = Boolean(auth.user.firstName?.trim() && auth.user.lastName?.trim());
  const hasEnabledOrganization = auth.user.organizations.some((org) => org.isEnabled);

  const { mutate, isPending } = useCreateOrganization();

  const isConnectedToSharepoint = sharepoint?.connected;
  const isConnectedToProcore = procore?.connected;

  return (
    <>
      <Form
        onFormSubmit={() =>
          mutate(
            { name: organizationName },
            {
              onSuccess: () => {
                setStep(0);
                setOrganizationName('');
                navigate({ to: '/choose-organization' });
              },
            },
          )
        }
        className="space-y-3 md:space-y-5 grow flex flex-col"
      >
        <Fieldset>
          <FieldsetLegend>
            {t({ en: 'Configure your organisation', nb: 'Konfigurer din organisasjon' })}
          </FieldsetLegend>
          <Field>
            <FieldLabel>{t({ en: 'Organisation name', nb: 'Organisasjonsnavn' })}</FieldLabel>
            <FieldControl
              value={organizationName}
              onValueChange={setOrganizationName}
              required
              placeholder={t({ en: 'Organisation name', nb: 'Organisasjonsnavn' })}
              maxLength={60}
            />
            <FieldError />
          </Field>
        </Fieldset>
        <div className="space-y-2">
          <p className="text-sm mb-4">
            {t({
              en: 'Connect your document management system (optional)',
              nb: 'Koble til ditt dokumenthåndteringssystem (valgfritt)',
            })}
          </p>
          {isLoading ? (
            <div className="py-4">
              <Spinner />
            </div>
          ) : (
            <div className="space-y-3">
              <DmsConnectRow
                icon={<Icons.sharepoint className="size-6" />}
                title={t({ en: 'SharePoint', nb: 'SharePoint' })}
                connected={!!isConnectedToSharepoint}
                subtitle={
                  isConnectedToSharepoint
                    ? t({
                        en: `Connected as ${sharepoint.displayName}`,
                        nb: `Koblet til som ${sharepoint.displayName}`,
                      })
                    : t({
                        en: 'Allow Volve to access your files',
                        nb: 'Gi Volve tilgang til filene dine',
                      })
                }
                dialogTitle={t({ en: 'Connect to SharePoint', nb: 'Koble til SharePoint' })}
                dialogDescription={t({
                  en: 'Make your SharePoint documents / files accessible in Volve. Doing this make analysing and importing document versions much easier.',
                  nb: 'Gjør dine SharePoint-dokumenter og filer tilgjengelige i Volve. Dette gjør det mye enklere å analysere og importere dokumentversjoner.',
                })}
                onConnect={sharepointLogin}
              />
              {PROCORE_ENABLED && (
                <DmsConnectRow
                  icon={<Icons.procore className="size-6" />}
                  title={t({ en: 'Procore', nb: 'Procore' })}
                  connected={!!isConnectedToProcore}
                  subtitle={
                    isConnectedToProcore
                      ? t({
                          en: `Connected as ${procore.displayName ?? ''}`,
                          nb: `Koblet til som ${procore.displayName ?? ''}`,
                        })
                      : t({
                          en: 'Allow Volve to access your files',
                          nb: 'Gi Volve tilgang til filene dine',
                        })
                  }
                  dialogTitle={t({ en: 'Connect to Procore', nb: 'Koble til Procore' })}
                  dialogDescription={t({
                    en: 'Make your Procore documents / files accessible in Volve. Doing this make analysing and importing documents much easier.',
                    nb: 'Gjør dine Procore-dokumenter og filer tilgjengelige i Volve. Dette gjør det mye enklere å analysere og importere dokumenter.',
                  })}
                  onConnect={procoreLogin}
                />
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 mt-auto gap-2">
          <Button
            type="button"
            size="lg"
            variant="secondary"
            onClick={() => {
              if (hasName || hasEnabledOrganization) {
                navigate({ to: hasEnabledOrganization ? '/' : '/choose-organization' });
                return;
              }
              setStep((prev) => prev - 1);
            }}
          >
            {t(common.back)}
          </Button>
          <Button type="submit" size="lg" pending={isPending}>
            {t(common.continue)}
            <Icon icon={ArrowRight02Icon} />
          </Button>
        </div>
      </Form>
    </>
  );
}

function DmsConnectRow({
  icon,
  title,
  subtitle,
  connected,
  dialogTitle,
  dialogDescription,
  onConnect,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  connected: boolean;
  dialogTitle: string;
  dialogDescription: string;
  onConnect: () => void;
}) {
  return (
    <div className="flex gap-4 sm:items-center max-sm:flex-col">
      <div className="flex gap-3 items-center">
        <div className="bg-surface-3 rounded-md grid size-10 place-items-center">{icon}</div>
        <div>
          <p className="font-medium text-sm">{title}</p>
          <p className="text-muted text-sm">{subtitle}</p>
        </div>
      </div>
      {connected ? (
        <Button disabled variant="secondary" kind="with-icon" className="sm:ml-auto max-sm:w-full">
          <Icon icon={Tick02Icon} />
          {t({ en: 'Connected', nb: 'Tilkoblet' })}
        </Button>
      ) : (
        <AlertDialog>
          <AlertDialogTrigger
            render={
              <Button className="sm:ml-auto max-sm:w-full">
                {t({ en: 'Connect', nb: 'Koble til' })}
              </Button>
            }
          />
          <AlertDialogPopup>
            <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>{dialogDescription}</AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel />
              <Button onClick={onConnect}>{t({ en: 'Connect', nb: 'Koble til' })}</Button>
            </AlertDialogFooter>
          </AlertDialogPopup>
        </AlertDialog>
      )}
    </div>
  );
}
